import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { supabase } from '@/lib/supabase'
import { Bell, Search, User, MessageCircle, Copy, Check } from 'lucide-react'
import { useAuth } from '@/features/auth'

interface HeaderProps {
  title: string
  subtitle?: string
}

interface Patient {
  id: string
  full_name: string
  birth_date?: string
  status: string
}

export function Header({ title, subtitle }: HeaderProps) {
  const navigate = useNavigate()
  const { userProfile, currentOrganization, isAdmin } = useAuth()
  const [notifications] = useState(0)
  const [whatsappCopied, setWhatsappCopied] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<Patient[]>([])
  const [showResults, setShowResults] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  // Busca de pacientes
  useEffect(() => {
    const searchPatients = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([])
        setShowResults(false)
        return
      }

      if (!currentOrganization?.id) return

      setIsSearching(true)
      try {
        const { data, error } = await supabase
          .from('patients')
          .select('id, full_name, birth_date, status')
          .eq('organization_id', currentOrganization.id)
          .ilike('full_name', `%${searchTerm}%`)
          .eq('status', 'active')
          .limit(5)

        if (error) throw error

        setSearchResults((data as Patient[]) || [])
        setShowResults(true)
      } catch (error) {
        console.error('Erro na busca:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    const debounceTimer = setTimeout(searchPatients, 300)
    return () => clearTimeout(debounceTimer)
  }, [searchTerm, currentOrganization?.id])

  // Fechar resultados ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  const handlePatientSelect = (patientId: string) => {
    setSearchTerm('')
    setShowResults(false)
    navigate({ to: '/patients/$patientId', params: { patientId } })
  }

  const handleCopyWhatsApp = async () => {
    await navigator.clipboard.writeText('+5511936204334')
    setWhatsappCopied(true)
    setTimeout(() => setWhatsappCopied(false), 2000)
  }

  return (
    <header className="content border-b border-gray-200 px-4 md:px-6 py-4 bg-white shadow-sm">
      <div className="flex items-center justify-between">
        {/* Title */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">{title}</h1>
            {subtitle && <p className="text-xs md:text-sm text-gray-500 mt-1.5 md:mt-2">{subtitle}</p>}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Search - Hidden on very small screens */}
          <div className="relative hidden sm:block" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar pacientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
              className="input pl-10 pr-4 py-2 w-40 md:w-64 text-sm border-gray-200 focus:border-teal-300 focus:ring-teal-300"
            />

            {/* Dropdown de resultados */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-80 overflow-y-auto">
                {isSearching ? (
                  <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500 mr-2"></div>
                    Buscando...
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                      Pacientes encontrados
                    </div>
                    {searchResults.map((patient) => {
                      const age = calculateAge(patient.birth_date)
                      return (
                        <button
                          key={patient.id}
                          onClick={() => handlePatientSelect(patient.id)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:bg-gray-50 focus:outline-none"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900 text-sm">
                                {patient.full_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {age ? `${age} anos` : 'Idade não informada'}
                              </div>
                            </div>
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Ativo
                              </span>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </>
                ) : searchTerm.length >= 2 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    Nenhum paciente encontrado para "{searchTerm}"
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Notifications */}
          <button className="relative p-2 rounded-lg hover:bg-teal-50 transition-colors">
            <Bell className="w-5 h-5 text-teal-600" />
            {notifications > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center shadow-sm">
                {notifications > 9 ? '9+' : notifications}
              </span>
            )}
          </button>

          {/* WhatsApp - Desktop (lg+): completo com texto */}
          <button
            onClick={handleCopyWhatsApp}
            className="hidden lg:flex items-center gap-2.5 px-3.5 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors group"
            title="Clique para copiar o número do WhatsApp"
          >
            <MessageCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <div className="flex flex-col items-start">
              <span className="text-[10px] leading-tight text-emerald-600/70">
                Compartilhe com os familiares
              </span>
              <span className="text-xs font-semibold text-emerald-700">
                (11) 93620-4334
              </span>
            </div>
            {whatsappCopied ? (
              <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-emerald-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" />
            )}
          </button>

          {/* WhatsApp - Tablet/Mobile (compacto) */}
          <button
            onClick={handleCopyWhatsApp}
            className="lg:hidden relative p-2 rounded-lg hover:bg-emerald-50 transition-colors"
            title="Copiar número do WhatsApp"
          >
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            {whatsappCopied && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                <Check className="w-3 h-3" />
              </span>
            )}
          </button>

          {/* User Menu */}
          <Link
            to="/settings"
            className="flex items-center space-x-2 md:space-x-3 pl-2 md:pl-4 border-l border-gray-200 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-sm">
              <User className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800">
                {userProfile?.full_name || 'Carregando...'}
              </p>
              <p className="text-xs text-teal-600 font-medium">
                {isAdmin ? 'Administrador' : 'Profissional'}
              </p>
            </div>
          </Link>
        </div>
      </div>
    </header>
  )
}
